import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AppProvider } from "@/context/AppContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { HomePage } from "@/pages/HomePage";
import { CelulasPage } from "@/pages/CelulasPage";
import { CelulaDetalhesPage } from "@/pages/CelulaDetalhesPage";
import { MembrosPage } from "@/pages/MembrosPage";
import { EncontrosPage } from "@/pages/EncontrosPage";
import { EncontroDetalhesPage } from "@/pages/EncontroDetalhesPage";
import { CalendarioPage } from "@/pages/CalendarioPage";
import { LoginPage } from "@/pages/LoginPage";
import { AdminDashboardPage } from "@/pages/AdminDashboardPage";
import { IgrejaDashboardPage } from "@/pages/IgrejaDashboardPage";
import { AuthCallbackPage } from "@/pages/AuthCallbackPage";
import { CategoriasPage } from "@/pages/CategoriasPage";
import { Loader2 } from "lucide-react";

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin Route component
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdminMaster, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isAdminMaster) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Public Route - redirect to home if authenticated
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      
      {/* OAuth callback route - must be outside PublicRoute to handle token */}
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      
      {/* Protected routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout>
            <HomePage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/celulas" element={
        <ProtectedRoute>
          <Layout>
            <CelulasPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/celulas/:id" element={
        <ProtectedRoute>
          <Layout>
            <CelulaDetalhesPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/membros" element={
        <ProtectedRoute>
          <Layout>
            <MembrosPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/encontros" element={
        <ProtectedRoute>
          <Layout>
            <EncontrosPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/encontros/:encontroId" element={
        <ProtectedRoute>
          <Layout>
            <EncontroDetalhesPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/calendario" element={
        <ProtectedRoute>
          <Layout>
            <CalendarioPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/igreja-dashboard" element={
        <ProtectedRoute>
          <Layout>
            <IgrejaDashboardPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/categorias" element={
        <ProtectedRoute>
          <Layout>
            <CategoriasPage />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Admin routes */}
      <Route path="/admin" element={
        <AdminRoute>
          <Layout>
            <AdminDashboardPage />
          </Layout>
        </AdminRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AppProvider>
    </AuthProvider>
  );
}

export default App;
